import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

interface Props {
  value?: boolean | null;
  size?: number;
}

/**
 * Read-only rendering of a network's `rcs_verified` flag (UES-12612 design):
 * outline green check-circle when verified, outline red x-circle when not
 * (16px, as in the mockups), a dash when the backend has not sent the flag.
 * Shared by the carrier-facing NSX identifiers table and the Network Settings
 * Admin table; the NetLync-only setter is `RcsVerifiedCell` in NetworkTable.
 *
 * The icons carry `mx-auto` because Tailwind Preflight makes every `svg`
 * `display: block`, so a cell's `text-align: center` cannot move them — only
 * auto margins can. It is a no-op where the icon sits inside a shrink-to-fit
 * inline-flex wrapper (the NSX table), which centres via `text-align` instead.
 */
export const RcsVerifiedIndicator = ({ value, size = 16 }: Props) => {
  const { t } = useTranslation();

  if (value === undefined || value === null) {
    // role="img" so the label is exposed — ARIA drops aria-label on a
    // generic <span>, which would read as a bare dash.
    return (
      <span role="img" aria-label={t('rcsVerified_status_unknown')}>
        —
      </span>
    );
  }

  if (value) {
    return (
      <CheckCircle
        size={size}
        weight="regular"
        role="img"
        aria-label={t('rcsVerified_status_verified')}
        className="mx-auto text-(--success)"
      />
    );
  }

  return (
    <XCircle
      size={size}
      weight="regular"
      role="img"
      aria-label={t('rcsVerified_status_not_verified')}
      className="mx-auto text-(--error)"
    />
  );
};
